/**
 * The views out of the windows: panoramas of a garden, a meadow, a city… that the 3D view draws round the house,
 * seen through its windows and from outside. The app hangs a photo behind each window (Unity
 * OpeningFurnitureController.UpdateWindowView, chosen with «Выбери вид»); the web draws one panorama round the whole
 * project instead, so a view stays right from any window, any floor and from outside, and chooses it the same way.
 *
 * The panoramas are CC0 pictures from Poly Haven, prepared by tools/build-window-views.py into
 * public/hp/environment/views/<id>/ and described in windowViews.json (generated, never edited by hand).
 */
import { t, type TranslationKey } from '../i18n/I18n';
import generated from './windowViews.json';

export interface WindowView {
  id: string;
  title: string;
  /** The panorama's page at its source, and its licence. */
  source: string;
  license: string;
  /** How high over its ground the panorama was taken, in metres. */
  captureHeight: number;
  /** The direction the picker's picture looks: degrees right of the panorama's middle. */
  look: number;
  /** Where the sun is in the panorama (degrees right of its middle and over the horizon), its colour, and whether
   * it is a disc in a clear sky or a bright patch behind cloud. The scene's sun is aimed along it. */
  sun: { azimuth: number; elevation: number; colour: number[]; kind: string } | null;
  /** The ground by the tripod as a repeating tile `size` metres across, with its linear mean colour: what the ground
   * round the house wears before it turns into the panorama's own far ground. */
  ground: { size: number; mean: number[] };
  /** The ground and the sky on average; the sky's tints the house's shadow on the ground. */
  colours: { ground: string; sky: string };
  bytes: { panorama: number; preview: number; ground: number; thumb: number };
}

/** The titles in the interface language; the generated file carries the Russian ones tools/build-window-views.py gives. */
const TITLES: Record<string, TranslationKey> = {
  meadow: '#WindowViewMeadow', garden: '#WindowViewGarden', suburb: '#WindowViewSuburb', mountains: '#WindowViewMountains',
  winter: '#WindowViewWinter', city: '#WindowViewCity', 'city-evening': '#WindowViewCityEvening', beach: '#WindowViewBeach',
  'sea-sunset': '#WindowViewSeaSunset',
};

export const WINDOW_VIEWS: readonly WindowView[] = generated.views.map(view => ({ ...view, get title() { return TITLES[view.id] ? t(TITLES[view.id]) : view.title; } }));
/** No view: the plain background the orbit had before, and the sun from its usual corner. */
export const NO_WINDOW_VIEW = 'none';
/** The meadow: open all round, so it holds from every window and from outside. */
export const DEFAULT_WINDOW_VIEW = 'meadow';

export function windowViewById(id: string | null | undefined): WindowView | null {
  return WINDOW_VIEWS.find(view => view.id === id) ?? null;
}

/** A stored choice: a known view, «none», or the default for anything else (an old record, a removed view). */
export function parseWindowViewId(value: unknown): string {
  return value === NO_WINDOW_VIEW || (typeof value === 'string' && windowViewById(value)) ? value as string : DEFAULT_WINDOW_VIEW;
}

/** Degrees in [0, 360), whole ones: the picker turns a view in steps and shows the number. */
export function normaliseViewRotation(value: unknown): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : 0;
  return ((n % 360) + 360) % 360;
}

const BASE = '/hp/environment/views/';
export function windowViewFiles(view: Pick<WindowView, 'id'>): { panorama: string; preview: string; ground: string; thumb: string } {
  const folder = BASE + encodeURIComponent(view.id) + '/';
  return { panorama: folder + 'panorama.webp', preview: folder + 'preview.webp', ground: folder + 'ground.webp', thumb: folder + 'thumb.webp' };
}

/**
 * Headings are measured on the plan: 0° is north (−z, up on the 2D plan), 90° east (+x), growing clockwise, the way
 * a person turns right. A panorama's azimuth grows the same way: to the right of its middle. Unturned (rotation 0),
 * a view faces the direction its picker picture looks at north.
 */
export function viewHeadingOffset(view: Pick<WindowView, 'look'>, rotation: number): number {
  return rotation - view.look;
}

/** The plan heading of a world direction, in degrees. */
export function headingOf(x: number, z: number): number {
  return Math.atan2(x, -z) * 180 / Math.PI;
}

/** A unit world direction for a plan heading and an elevation, both in degrees. */
export function directionOf(heading: number, elevation: number): [number, number, number] {
  const h = heading * Math.PI / 180, e = elevation * Math.PI / 180;
  return [Math.sin(h) * Math.cos(e), Math.sin(e), -Math.cos(h) * Math.cos(e)];
}

/** Lighting keeps the sun between these heights: lower, the walls throw half a room into shade and shadows run off
 * the plan; higher, no sun comes in at all. A low morning or evening sun keeps its direction, not its height. */
export const SUN_ELEVATION_LIMITS = { min: 40, max: 65 };

/** Where the scene's sun stands for a view turned by `rotation`: the panorama's own sun, within the limits above. */
export function viewSunDirection(view: WindowView, rotation: number): [number, number, number] | null {
  if (!view.sun) return null;
  const elevation = Math.min(SUN_ELEVATION_LIMITS.max, Math.max(SUN_ELEVATION_LIMITS.min, view.sun.elevation));
  return directionOf(view.sun.azimuth + viewHeadingOffset(view, rotation), elevation);
}
